import { useQuery } from '@tanstack/react-query'
import { pfetch } from '@/lib/phishtrainer/play-api'
import { playKeys } from '@/lib/phishtrainer/query-keys'

// ============ Types ============

/** The enriched library payload: the attack catalogue plus this learner's rounds. */
export interface PlayLibraryData {
  rounds?: unknown[]
  [k: string]: unknown
}

// ============ Query Hooks ============

/**
 * The learner's enriched library — attack catalogue + their round history.
 *
 * ONE cache entry for a payload that was being fetched TWICE on a single
 * navigation: the /play/library page needs the whole thing, while
 * RecentRoundsCard prefetches the same response only to read `.rounds` off it.
 * Both now read this; the second consumer is a cache hit rather than a second
 * transfer of a payload it mostly discards.
 *
 * Generic so each consumer keeps its own richer type instead of the hook
 * flattening both to `unknown`.
 */
export function usePlayLibrary<T = PlayLibraryData>(userId: string | undefined) {
  return useQuery({
    queryKey: playKeys.library.list(undefined, userId),
    queryFn: async () => {
      const res = await pfetch(`/api/phishtrainer/users/${userId}/library`)
      if (!res.ok) throw new Error('Could not load your library')
      return res.json() as Promise<{ data?: T | null }>
    },
    select: (r: { data?: T | null }) => r.data ?? null,
    enabled: !!userId,
  })
}
